import { pluralCategory } from '@/i18n/format.ts'
import type { Counting } from './systems.ts'

/**
 * `admin.policies` — copy for the policy plane (PRD-18 AGM-100…106).
 *
 * Administrator vocabulary throughout — AGM-022 scopes the banned words to consumer and
 * maker surfaces, and hiding "retrieval depth" from the person whose job is to set retrieval
 * depth would be its own kind of dishonesty. What the copy DOES insist on is blast radius:
 * every screen here says how many agents an edit reaches, because a profile is the one
 * object on the platform whose single-field change alters every run that resolves through
 * it, within the config SLO, with no write to any agent row.
 *
 * `MODEL_ALIASES` and `RETRIEVAL_STRATEGIES` are gone from this file and live in
 * `components/marketplace/admin/policy-options.ts`. They are contract enums, not words
 * (PRD-27 F-20, D-25): a pt-BR copy that drifted on either would offer Portuguese readers a
 * different set of models than everybody else.
 */

/** The formatters a profile summary needs: a count, and a cost in the reader's notation. */
type PolicyFormat = Counting & { formatCost: (usd: number) => string }

export const policies = {
  title: 'Runtime profiles',
  lead: 'Model routing, retrieval tuning, budget ceilings, approval and delegation caps. An agent runs under exactly one profile; environment variables are only the bootstrap values of the built-in one.',
  loading: 'Loading profiles…',
  unavailable: 'The policy plane is not available on this deployment yet',
  unavailableBody: 'GET /api/admin/policies has not answered. Profile editing is disabled until it does.',
  empty: 'No profiles yet',
  emptyBody: 'The built-in `standard` profile is seeded from environment values on first boot.',

  builtin: 'built-in',
  /**
   * The blast radius, which is the number this whole plane is organised around — so it is
   * counted in the reader's language and carries both of its forms. `Intl.PluralRules` rather
   * than `count === 1`: Brazilian Portuguese puts ZERO in the singular category, and a profile
   * nothing is assigned to is the commonest state of a new one.
   */
  assigned: (fmt: Counting, count: number): string =>
    pluralCategory(fmt.locale, count) === 'one'
      ? `${fmt.formatCount(count)} agent`
      : `${fmt.formatCount(count)} agents`,
  version: (version: number): string => `v${version}`,
  edit: 'Edit',
  cancel: 'Cancel',
  newProfile: 'New profile',
  duplicate: 'Duplicate',

  /**
   * The one-line shape of a profile, under its name.
   *
   * It was assembled in the row's JSX out of five fragments, a bare `$` and a raw number —
   * which is a sentence built at a call site (LOC-013) wrapped around an unformatted currency
   * (F-2). The alias and the strategy are wire identifiers and stay in every language
   * (D-17); `top-k` stays English because it is a term of art (D-15).
   */
  rowSummary: (
    fmt: PolicyFormat,
    profile: { modelAlias: string; strategy: string; topK: number; maxSteps: number; maxCostUsd: number }
  ): string =>
    `${profile.modelAlias} · ${profile.strategy} · top-k ${fmt.formatCount(profile.topK)} · max ${fmt.formatCount(profile.maxSteps)} steps · ${fmt.formatCost(profile.maxCostUsd)}`,

  nameLabel: 'Name',
  namePlaceholder: 'high-assurance',
  descriptionLabel: 'Description',
  descriptionPlaceholder: 'What this profile is for, and which agents should run under it.',
  noteLabel: 'Change note (audited)',
  notePlaceholder: 'Why this is changing.',

  sections: {
    model: 'Model routing',
    retrieval: 'Retrieval',
    budget: 'Budget ceilings',
    approval: 'Approval',
    delegation: 'Delegation'
  },

  fields: {
    modelAlias: 'Model alias',
    strategy: 'Strategy',
    topK: 'Top-k',
    minScore: 'Minimum score',
    candidates: 'Candidates',
    maxSteps: 'Max steps',
    maxModelCalls: 'Max model calls',
    maxToolCalls: 'Max tool calls',
    maxDelegations: 'Max delegations',
    maxCostUsd: 'Max cost (USD)',
    maxWallClockMs: 'Max wall clock (ms)',
    ttlMs: 'Approval TTL (ms)',
    maxRounds: 'Max approval rounds',
    maxDepth: 'Max depth',
    maxParallel: 'Max parallel'
  },

  dryRun: 'Preview changes',
  dryRunning: 'Working out what changes…',
  dryRunTitle: 'What this edit would change',
  dryRunEmpty: 'No agent resolves differently after this edit.',
  dryRunAgents: (fmt: Counting, count: number): string =>
    pluralCategory(fmt.locale, count) === 'one'
      ? `${fmt.formatCount(count)} agent affected`
      : `${fmt.formatCount(count)} agents affected`,
  dryRunBefore: 'before',
  dryRunAfter: 'after',
  dryRunFailed: 'The preview could not be produced. Nothing was written.',
  dryRunRequired: 'Preview the change before saving — a save here reaches every agent under this profile.',

  save: 'Save profile',
  saving: 'Saving…',
  saved: 'Saved',
  saveFailed: 'The profile could not be saved.',
  versionConflict: 'Someone else saved this profile while you were editing. Reload before saving again.',

  delete: 'Delete profile',
  deleting: 'Deleting…',
  deleteConfirm: 'Delete this profile? Agents must be reassigned first.',
  deleteRefused: 'This profile is still in use',
  deleteRefusedBody: 'Reassign every agent below to another profile, then delete it. Nothing was changed.',
  deleteBuiltin: 'The built-in profile cannot be deleted.',
  deleteFailed: 'The profile could not be deleted.',

  assignmentsTitle: 'Assignments',
  assignmentsLead: 'One profile per agent. Changing an assignment takes effect on the agent’s next run.',
  assignmentsLoading: 'Loading assignments…',
  assignmentsEmpty: 'No agent has an explicit assignment — every agent runs under the built-in profile.',
  assignmentsUnavailable: 'Assignments are not available on this deployment yet.',
  agentColumn: 'Agent',
  profileColumn: 'Profile',
  reassign: 'Reassign',
  reassigning: 'Reassigning…',
  reassignFailed: 'That agent could not be reassigned.'
}
